import React from "react";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import classnames from "classnames";
import { format } from "date-fns";

import StatsCard from "common/Components/StatsCard";
import { Dropdown, DatePicker } from "@my-scoot/component-library-legacy";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import { convertStringToDateObject } from "features/Common/modules/DateTime/utils/DateTime.utils";
import { YEAR_MONTH_DAY_FORMAT } from "features/Common/modules/DateTime/DateTime.constants";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";
import { getS3AssetCDNUrl } from "features/Common/modules/File/modules/Upload/utils/FileUpload.utils";
import { date_range_select_options, STAT_CARDS } from "../../constants";
import { useSalesPageStats } from "./useSalesPageStats";
import {
  formatRevenue,
  SHOW_TOTAL_VISITORS,
} from "features/Common/utils/common.utils";

const StatsLoadingState = ({ color }) => (
  <CircularProgress size={18} color={color} />
);

export const SalesPageStats = withComponentLibraryTheme(() => {
  const isDesktop = useMediaQuery("(min-width: 767px)");
  const {
    performanceData,
    selectedRange,
    onRangeChange,
    dataRange,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
  } = useSalesPageStats();

  return (
    <Box className="tw-mb-8">
      <div
        className={classnames(
          "tw-mb-[18px] tw-flex",
          selectedRange === date_range_select_options.custom.value
            ? "tw-flex-col md:tw-flex-row tw-gap-3 md:tw-gap-0 md:tw-items-center"
            : "tw-flex-row-reverse md:tw-flex-row tw-justify-between md:tw-justify-normal tw-items-center"
        )}
      >
        <Dropdown
          options={Object.values(date_range_select_options)}
          value={selectedRange}
          handleChange={onRangeChange}
          placeholder="Select date range"
          className="mr-md-4 !tw-h-[40px] !tw-w-[153px] tw-border-[1.5px] tw-border-solid tw-border-[#cfcdc9] tw-rounded-[5px]"
        />

        {selectedRange !== date_range_select_options.custom.value ? (
          <p className="!tw-m-0 tw-text-[16px] tw-font-medium tw-tracking-[0.64px] tw-flex tw-items-center">
            {dataRange}
          </p>
        ) : (
          <div className="tw-flex tw-w-full md:tw-w-auto tw-items-center">
            <DatePicker
              size={!isDesktop ? "thin" : "small"}
              popperClassName="!tw-z-[11]"
              placeholder="From date"
              value={
                startDate
                  ? convertStringToDateObject({ dateString: startDate })
                  : null
              }
              onChange={(date) =>
                setStartDate(format(date, YEAR_MONTH_DAY_FORMAT))
              }
              wrapperClassName="tw-w-1/2 md:tw-w-[140px] tw-mr-2 !tw-h-[40px] tw-bg-white"
            />
            <DatePicker
              popperPlacement={isDesktop ? "bottom-end" : ""}
              size={!isDesktop ? "thin" : "small"}
              wrapperClassName={classnames(
                "tw-w-1/2 md:tw-w-[140px] md:tw-ml-2 !tw-h-[40px] tw-bg-white",
                {
                  "!tw-pointer-events-none": !startDate,
                }
              )}
              popperClassName="!tw-z-[11]"
              minDate={
                startDate
                  ? convertStringToDateObject({ dateString: startDate })
                  : null
              }
              disabled={!startDate}
              placeholder="To date"
              value={
                endDate
                  ? convertStringToDateObject({ dateString: endDate })
                  : null
              }
              onChange={(date) =>
                setEndDate(format(date, YEAR_MONTH_DAY_FORMAT))
              }
            />
          </div>
        )}
      </div>

      <div className="tw-flex tw-w-full tw-gap-[15px] tw-overflow-x-auto md:tw-overflow-visible">
        {SHOW_TOTAL_VISITORS && (
          <StatsCard
            className="md:tw-mb-0 tw-mb-[16px]"
            color="persian_blue"
            linkText=""
            title={STAT_CARDS.VISITORS.title}
            tooltip="Number of people who visited your webpage or offering pages"
            statsNumber={
              performanceData ? (
                performanceData?.total_visitors
              ) : (
                <StatsLoadingState color="persian_blue" />
              )
            }
            Icon={({ className }) => (
              <PeopleAltIcon
                className={classnames("!tw-text-[32px]", className)}
              />
            )}
          />
        )}

        <StatsCard
          className="md:tw-mb-0 tw-mb-[16px]"
          color="primary"
          linkText=""
          title={STAT_CARDS.BOOKINGS.title}
          tooltip="Number of bookings received via all tracking links"
          statsNumber={
            performanceData ? (
              performanceData?.total_bookings
            ) : (
              <StatsLoadingState color="primary" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/booking.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
        />

        <StatsCard
          className="md:tw-mb-0 tw-mb-[16px]"
          color="fine_pine"
          linkText=""
          title={STAT_CARDS.REVENUE.title}
          tooltip="Total revenue received via all tracking links"
          statsNumber={
            performanceData ? (
              formatRevenue(performanceData?.total_revenue)
            ) : (
              <StatsLoadingState color="fine_pine" />
            )
          }
          Icon={() => (
            <ExlyImage
              src={getS3AssetCDNUrl("assets/vectors/dashboard/revenue.png")}
              width={28}
              height={28}
              className="!tw-mr-[16px]"
            />
          )}
        />
      </div>
    </Box>
  );
});
